import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  cn(
"relative w-full rounded-tile border px-3 py-2.5 text-sm text-dim scrim",
"[&>svg~*]:pl-6 [&>svg+div]:translate-y-[-1px]",
"[&>svg]:absolute [&>svg]:left-3 [&>svg]:top-3 [&>svg]:size-3.5 [&>svg]:drop-scrim",
  ),
  {
    variants: {
      variant: {
        default: "border-edge bg-tile [&>svg]:text-faint",
        warning: "border-amber/45 bg-amber/10 [&>svg]:text-amber",
        info: "border-thirst/45 bg-thirst/10 [&>svg]:text-thirst-soft",
        success: "border-success/45 bg-success/10 [&>svg]:text-success",
        destructive: "border-danger/45 bg-danger/10 [&>svg]:text-danger-soft",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-highlight text-caption uppercase leading-none tracking-[.14em] text-fg scrim", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-dim [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
